import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router';

import { CompanyScopeNote } from '@/components/documents/CompanyScopeNote';
import { avatarColorIndex, nameInitials } from '@/components/ui/avatar';
import { CardFrame, CardNote } from '@/components/ui/card-frame';
import { cn } from '@/components/ui/cn';
import { errorCopy, readerBadge } from '@/features/training/presentation';
import type { DocumentReaders, Reader } from '@/features/training/types';
import { useDocumentReaders } from '@/features/training/use-training';
import { usePermission } from '@/hooks/usePermission';
import { apiErrorReason, ApiError } from '@/lib/api';
import { useBackState } from '@/lib/back-nav';
import { useDocumentCompanyAccess } from '@/lib/document-company';
import { plural } from '@/lib/plural';
import { skeletonLine } from '@/components/ui/skeleton';

// Panel "Capacitación asociada" de la ficha (spec 2026-10-14 §4, mockup 03): quién debe
// leer la versión vigente y en qué estado está, lo urgente primero, y el paso a la
// pantalla Capacitaciones con regreso a la ficha. Datos del LMS vía so=360, bajo la
// empresa activa: si no es la del documento, lo explica en vez de consultar.

const SHOWN = 5;
const ORDER: Record<Reader['state'], number> = { overdue: 0, pending: 1, read: 2 };

/** Por qué un documento no vigente no muestra lectores. */
function notCurrentText(status: string): string {
  if (status === 'obsoleto') {
    return 'El documento está obsoleto: su lectura ya no se asigna.';
  }
  if (status === 'anulado') {
    return 'El código se anuló antes de elaborarse: no tiene capacitación.';
  }
  // G4: las lecturas se asignan al firmarse la aprobación; el avance de esa versión se
  // ve en la etapa de capacitación. Esta lista muestra los lectores de la vigente.
  return 'Las lecturas se asignan al firmarse la aprobación; su avance se ve en la etapa de capacitación.';
}

/** "N de M leídos · X vencidos" con los conteos exactos del API; null si no se conocen. */
function summary(totals: DocumentReaders['totals']): string | null {
  if (totals === null) {
    return null;
  }
  const head = `${totals.read} de ${totals.assigned} ${totals.assigned === 1 ? 'leído' : 'leídos'}`;
  return totals.overdue > 0 ? `${head} · ${plural(totals.overdue, 'vencido', 'vencidos')}` : head;
}

function Frame({ children }: { children: ReactNode }): ReactElement {
  return (
    <CardFrame title="Capacitación asociada" count="LMS" className="d2">
      {children}
    </CardFrame>
  );
}

export function AssociatedTrainingCard({
  entryId,
  code,
  status,
  companyId,
}: {
  entryId: string;
  code: string;
  status: string;
  /** Empresa del documento (la consulta va bajo la empresa activa). */
  companyId: string;
}): ReactElement | null {
  const canRead = usePermission('quality:training:read');
  const access = useDocumentCompanyAccess(companyId);
  const vigente = status === 'vigente';
  const query = useDocumentReaders(entryId, {
    companyId: access.kind === 'active' ? companyId : null,
    enabled: canRead && vigente && access.kind === 'active',
  });
  const fromHere = useBackState(`Ficha ${code}`);

  if (!canRead) {
    return null;
  }
  if (!vigente) {
    return (
      <Frame>
        <CardNote>{notCurrentText(status)}</CardNote>
      </Frame>
    );
  }
  if (access.kind !== 'active') {
    return (
      <Frame>
        <CompanyScopeNote access={access} subject="su capacitación" />
      </Frame>
    );
  }
  if (query.isError) {
    const err = query.error;
    if (err instanceof ApiError && err.status === 404 && apiErrorReason(err) === 'no_training') {
      return (
        <Frame>
          <CardNote>La versión vigente no tiene capacitación registrada en el LMS.</CardNote>
        </Frame>
      );
    }
    const copy = errorCopy(err);
    return (
      <Frame>
        <div className="cb" role="alert">
          <p style={{ margin: 0, fontWeight: 600, fontSize: 13 }}>{copy.title}</p>
          <p className="soft" style={{ margin: '4px 0 0', fontSize: 12.5 }}>
            {copy.body}
          </p>
          {copy.retry && (
            <button
              type="button"
              className="btn mut sm"
              style={{ marginTop: 10 }}
              onClick={() => void query.refetch()}
            >
              Reintentar
            </button>
          )}
        </div>
      </Frame>
    );
  }
  if (query.data === undefined) {
    return (
      <Frame>
        <div className="cb" aria-busy="true">
          <span className="sr-only">Cargando la capacitación</span>
          {Array.from({ length: 3 }, (_, i) => (
            <div key={i} className={skeletonLine({ width: 90, space: 'y' })} aria-hidden="true" />
          ))}
        </div>
      </Frame>
    );
  }
  const all = query.data.readers;
  if (all.length === 0) {
    return (
      <Frame>
        <CardNote>
          Nadie tiene asignada la lectura de la versión vigente en la empresa activa.
        </CardNote>
      </Frame>
    );
  }
  const { totals } = query.data;
  const shown = [...all].sort((a, b) => ORDER[a.state] - ORDER[b.state]).slice(0, SHOWN);
  const head = summary(totals);
  const rest = totals === null ? null : totals.assigned - shown.length;
  return (
    <Frame>
      {head !== null && <p className="assoc-summary">{head}</p>}
      <ul className="assoc-list">
        {shown.map((r) => {
          const badge = readerBadge(r);
          return (
            <li key={r.lmsUserId} className="assoc-row">
              <span className={cn('av sm', `p${avatarColorIndex(r.name)}`)} aria-hidden="true">
                {nameInitials(r.name)}
              </span>
              <span className="nm">{r.name}</span>
              <span className={cn('badge', badge.cls)}>{badge.label}</span>
            </li>
          );
        })}
      </ul>
      <div className="assoc-foot">
        {rest === null ? (
          <span className="soft">Hay más personas asignadas.</span>
        ) : (
          rest > 0 && <span className="soft">{`y ${plural(rest, 'persona', 'personas')} más`}</span>
        )}
        <Link
          to={`/capacitaciones?doc=${encodeURIComponent(entryId)}`}
          state={fromHere}
          className="btn ghost sm"
        >
          Ver capacitación →
        </Link>
      </div>
    </Frame>
  );
}
